/**
 * useCamera — manages getUserMedia video stream and frame capture.
 *
 * Captures JPEG frames from the camera at a configurable FPS.
 * Designed for mobile: requests rear camera, 1280x720.
 */

import { useRef, useState, useCallback, useEffect } from 'react'

interface UseCameraOptions {
  targetFps?: number
  quality?: number // JPEG quality 0-1
  maxDimension?: number
}

interface CapturedFrame {
  blob: Blob
  index: number
  timestamp: number
}

export function useCamera(options: UseCameraOptions = {}) {
  const { targetFps = 2, quality = 0.85, maxDimension = 800 } = options

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const intervalRef = useRef<number | null>(null)
  const frameIndexRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)

  const [isStreaming, setIsStreaming] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [frames, setFrames] = useState<CapturedFrame[]>([])
  const [error, setError] = useState<string | null>(null)

  // Attach stream to video element when it becomes available (via ref)
  // This handles the case where getUserMedia runs before the <video> is mounted.
  useEffect(() => {
    const video = videoRef.current
    const stream = streamRef.current
    if (!video || !stream || video.srcObject === stream) return

    video.srcObject = stream
    video.play()
      .then(() => setIsStreaming(true))
      .catch((err) => {
        console.error('Video play error:', err)
        setError('Could not play video stream')
      })
  })

  // Start camera stream — gets the stream; attaching to <video> happens via effect
  const startCamera = useCallback(async () => {
    try {
      setError(null)

      // mediaDevices requires HTTPS on mobile browsers
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError(
          'Camera API not available. Make sure you are using HTTPS. ' +
          'Open https://' + window.location.host + '/scan instead.'
        )
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }, // Rear camera
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })

      streamRef.current = stream

      // If video element is already mounted, attach now
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
        setIsStreaming(true)
      }
      // Otherwise the effect above will attach it once <video> mounts
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access denied'
      setError(msg)
      console.error('Camera error:', err)
    }
  }, [])

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject = null
    }
    setIsStreaming(false)
  }, [])

  // Capture a single frame from the video
  const captureFrame = useCallback((): CapturedFrame | null => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState < 2) return null

    // Scale to maxDimension while keeping aspect ratio
    const vw = video.videoWidth
    const vh = video.videoHeight
    const scale = Math.min(maxDimension / Math.max(vw, vh), 1)
    const w = Math.round(vw * scale)
    const h = Math.round(vh * scale)

    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(video, 0, 0, w, h)

    // Convert to JPEG blob synchronously via toDataURL (more reliable on mobile)
    const dataUrl = canvas.toDataURL('image/jpeg', quality)
    const binary = atob(dataUrl.split(',')[1])
    const array = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i)
    const blob = new Blob([array], { type: 'image/jpeg' })

    const frame: CapturedFrame = {
      blob,
      index: ++frameIndexRef.current,
      timestamp: Date.now(),
    }
    return frame
  }, [maxDimension, quality])

  // Start auto-capture at targetFps
  const startCapture = useCallback(() => {
    if (!isStreaming || isCapturing) return

    frameIndexRef.current = 0
    setFrames([])
    setIsCapturing(true)

    const intervalMs = 1000 / targetFps
    intervalRef.current = window.setInterval(() => {
      const frame = captureFrame()
      if (frame) {
        setFrames((prev) => [...prev, frame])
      }
    }, intervalMs)
  }, [isStreaming, isCapturing, targetFps, captureFrame])

  // Stop auto-capture
  const stopCapture = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsCapturing(false)
  }, [])

  // Remove a single frame by index
  const removeFrame = useCallback((index: number) => {
    setFrames((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // Clear captured frames
  const clearFrames = useCallback(() => {
    setFrames([])
    frameIndexRef.current = 0
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCapture()
      stopCamera()
    }
  }, [stopCapture, stopCamera])

  return {
    videoRef,
    canvasRef,
    isStreaming,
    isCapturing,
    frames,
    error,
    startCamera,
    stopCamera,
    startCapture,
    stopCapture,
    removeFrame,
    clearFrames,
  }
}
